import { Component, ElementRef, HostListener, inject, Renderer2, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { AppConfigService } from '@core/services/app-config/app-config.service';
import { MessageModule } from 'primeng/message';
import { ScrollTop } from 'primeng/scrolltop';
import { BadgeModule } from 'primeng/badge';
import { StyleClassModule } from 'primeng/styleclass';
import { DebugPanelComponent } from '@shared/component/debug-panel/debug-panel.component';
import { AppSidebar } from '@shared/component/sidebar/sidebar.component';
import { Breadcrumb } from 'primeng/breadcrumb';
import { MenuItem } from 'primeng/api';
import { filter } from 'rxjs';
import { AuthAPIService } from '@services/AuthService/auth.api.service';
import { UserLocalService } from '@services/UserService/user-local.service';
import { AvatarComponent } from '@shared/component/general/avatar/avatar.component';

@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        AvatarComponent,
        BadgeModule,
        MessageModule,
        ScrollTop,
        StyleClassModule,
        AppSidebar,
        Breadcrumb,
        DebugPanelComponent
    ],
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss']
})
export class AppLayout {
    @ViewChild('profileContainer') profileContainer!: ElementRef;
    @ViewChild('layoutTopbar') layoutTopbar?: ElementRef<HTMLElement>;

    private topbarResizeObserver?: ResizeObserver;

    items: MenuItem[] = [];
    home: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

    profileMenuVisible = false;
    sidebarVisible = true;

    private appConfigService = inject(AppConfigService);
    readonly environmentName = this.appConfigService.get('environmentName');
    readonly currentYear = new Date().getFullYear();

    constructor(
        private authAPIService: AuthAPIService,
        private userService: UserLocalService,
        public renderer: Renderer2,
        public router: Router,
        private route: ActivatedRoute
    ) {
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.items = this.buildBreadcrumb(this.route.root);
        });
    }

    @HostListener('document:click', ['$event'])
    handleOutsideClick(event: Event) {
        if (!this.profileMenuVisible) return;

        if (this.profileContainer && !this.profileContainer.nativeElement.contains(event.target)) {
            this.profileMenuVisible = false;
        }
    }

    ngOnInit() {
        // Build it now; the first NavigationEnd was missed.
        this.items = this.buildBreadcrumb(this.route.root);

        // Only a real page change scrolls to top. Filter changes only touch the query string.
        let lastPath = this.router.url.split('?')[0];
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event) => {
                const path = (event as NavigationEnd).urlAfterRedirects.split('?')[0];
                if (path === lastPath) return;
                lastPath = path;
                this.scrollToTop();
                document.getElementById('main-content')?.focus({ preventScroll: true });
            });
    }

    /**
     * Publishes the sticky topbar's height as --topbar-height on the scroll
     * container, so page content that is itself sticky (e.g. the statement
     * page's options rail) can stick just below the header instead of sliding
     * under it. The height varies (environment banner, wrapping), hence a
     * ResizeObserver rather than a constant.
     */
    ngAfterViewInit() {
        const topbar = this.layoutTopbar?.nativeElement;
        if (!topbar || typeof ResizeObserver === 'undefined') return;

        const publishHeight = () =>
            topbar.parentElement?.style.setProperty('--topbar-height', `${topbar.offsetHeight}px`);
        publishHeight();
        this.topbarResizeObserver = new ResizeObserver(publishHeight);
        this.topbarResizeObserver.observe(topbar);
    }

    ngOnDestroy() {
        this.topbarResizeObserver?.disconnect();
    }

    private scrollToTop() {
        window.scrollTo(0, 0);

        const mainContent = document.querySelector('.main-content');
        if (mainContent) {
            mainContent.scrollTop = 0;
        }

        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    }

    isProduction(): boolean {
        return this.appConfigService.get('production');
    }

    toggleProfileMenu() {
        this.profileMenuVisible = !this.profileMenuVisible;

        if (window.innerWidth < 1024) {
            const sidebar = document.getElementById('app-sidebar-1');
            if (sidebar) {
                sidebar.classList.add('hidden');
            }
        }
    }

    private buildBreadcrumb(
        route: ActivatedRoute,
        url: string = '',
        breadcrumbs: MenuItem[] = []
    ): MenuItem[] {
        const children: ActivatedRoute[] = route.children;

        if (children.length === 0) {
            return breadcrumbs;
        }

        for (const child of children) {
            const routeURL: string = child.snapshot.url.map((segment) => segment.path).join('/');
            if (routeURL) {
                url += `/${routeURL}`;
            }

            const label = child.snapshot.data['breadcrumb'];
            if (label) {
                breadcrumbs.push({ label, routerLink: url });
            }

            this.buildBreadcrumb(child, url, breadcrumbs);
        }

        return breadcrumbs;
    }

    logout() {
        this.authAPIService.logout();
    }

    toggleSidebar() {
        const sidebar = document.getElementById('app-sidebar-1');
        if (!sidebar) return;

        if (sidebar.classList.contains('lg:block')) {
            // HIDE
            sidebar.classList.remove('lg:block');
            sidebar.classList.add('hidden');
        } else {
            // SHOW
            sidebar.classList.remove('hidden');
            sidebar.classList.add('lg:block');
        }
    }

    get displayName(): string {
        return this.userService.displayName;
    }
}
